import React, { useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight, FileText, FileEdit, Printer, Smartphone, ArrowRight } from 'lucide-react';
const items = [{
  num: '01',
  emoji: '📖',
  pages: '+200 páginas',
  title: 'Los 6 Volúmenes del Método',
  desc: 'Guía completa con explicaciones, dibujos técnicos y progresiones graduales.',
  format: 'PDF + Word'
}, {
  num: '02',
  emoji: '📋',
  pages: '40+ páginas',
  title: 'Kit Táctico Pro',
  desc: 'Playbooks visuales, pizarras imprimibles y sistemas de rotación (5-1, 4-2, 6-0).',
  format: 'PDF + Word'
}, {
  num: '03',
  emoji: '💪',
  pages: '80+ páginas',
  title: 'Manual de Preparación Física',
  desc: 'Secuencias dinámicas que integran fuerza, salto, potencia y prevención de lesiones.',
  format: 'PDF + Word'
}, {
  num: '04',
  emoji: '🗂️',
  pages: '30+ páginas',
  title: 'Fichas de Scout y Evaluación',
  desc: 'Tarjetas individuales para medir el rendimiento de cada jugador en cancha.',
  format: 'PDF + Word'
}];
const formatChips = [{
  icon: FileText,
  label: 'PDF de alta calidad'
}, {
  icon: FileEdit,
  label: 'Word editable'
}, {
  icon: Printer,
  label: 'Imprimible en A4'
}, {
  icon: Smartphone,
  label: 'Tablet & móvil'
}];
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/aiSKHFDPhcWjosb3wih7Fa/Captura_de_pantalla_2026-05-24_a_las_11.43.03_p.webp',
  alt: 'Capítulo 2 · El Cerebro del Niño Deportista — Principios de Aprendizaje'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/aTvUNP265F9mzD94Fu7n2a/Captura_de_pantalla_2026-05-24_a_las_11.49.50_p.webp',
  alt: 'Prof. Valentina Ríos — Entrenadora Nacional certificada CSV'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/4dTgiy7WCny9i4MqivJWXJ/Captura_de_pantalla_2026-05-24_a_las_11.50.10_p.webp',
  alt: 'Sustituciones Estratégicas: Ofensiva, Defensiva y de Momentum'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gLY3ZLn15K9f3SCMbVMdzT/Captura_de_pantalla_2026-05-24_a_las_11.50.42_p.webp',
  alt: 'Diferencias Nutricionales: Pretemporada vs. Temporada Competitiva'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rqC947bR2GaBUCUbVn9QuF/Captura_de_pantalla_2026-05-24_a_las_11.50.57_p.webp',
  alt: 'Drills 13 al 15 · Mecánica del Pase con progresión por nivel'
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const center = container.scrollLeft + container.clientWidth / 2;
    let minDistance = Infinity;
    let active = 0;
    Array.from(container.children).forEach((child, index) => {
      const childCenter = (child as HTMLElement).offsetLeft + (child as HTMLElement).clientWidth / 2;
      const distance = Math.abs(center - childCenter);
      if (distance < minDistance) {
        minDistance = distance;
        active = index;
      }
    });
    setActiveIndex(active);
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => {
    if (activeIndex > 0) scrollToIndex(activeIndex - 1);
  };
  const scrollNext = () => {
    if (activeIndex < carouselImages.length - 1) scrollToIndex(activeIndex + 1);
  };
  return <section className="w-full bg-bg-dark py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-55">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-primary/10 rounded-full blur-[120px] pointer-events-none hidden sm:block" data-id="element-56"></div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-57">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12" data-id="element-58">
          <p className="inline-block bg-primary/10 text-primary font-bold uppercase tracking-wider text-[11px] sm:text-xs px-3 py-1.5 rounded-full border border-primary/20 mb-4" data-id="element-59">
            📦 Acceso inmediato
          </p>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main mb-4 tracking-tight" data-id="element-60">
            Esto recibes en tu correo{' '}
            <span className="text-primary" data-id="element-61">hoy mismo</span>.
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto" data-id="element-62">
            <span className="text-text-main font-bold" data-id="element-63">
              +350 páginas de material profesional
            </span>{' '}
            descargables al instante, sin esperar envíos.
          </p>

          {/* Format chips */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-6" data-id="element-64">
            {formatChips.map((c, i) => {
            const Icon = c.icon;
            return <span key={i} className="inline-flex items-center gap-1.5 bg-bg-alt border border-border-light text-text-main text-[11px] sm:text-xs font-bold px-2.5 py-1.5 rounded-full" data-id="element-65">
                  <Icon className="w-3.5 h-3.5 text-cta-green" data-id="element-66" />
                  {c.label}
                </span>;
          })}
          </div>
        </div>

        {/* Content cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6" data-id="element-67">
          {items.map((item, i) => <div key={i} className="bg-bg-alt rounded-2xl border border-border-light overflow-hidden flex flex-col relative group hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_18px_40px_-15px_rgba(30,64,175,0.15)] transition-all duration-300 shadow-[0_4px_18px_-10px_rgba(30,64,175,0.08)]" data-id="element-68">
              {/* Blue top bar */}
              <div className="bg-gradient-to-r from-primary via-primary to-blue-400 h-1 w-full" data-id="element-69" />

              <div className="flex-1 flex flex-col p-6 sm:p-7 relative" data-id="element-70">
                {/* Number badge top-right */}
                <span className="absolute top-5 right-5 text-primary/10 font-black text-3xl sm:text-4xl leading-none select-none" data-id="element-71">
                  {item.num}
                </span>

                <span className="text-4xl sm:text-5xl mb-3 block relative z-10" data-id="element-72">
                  {item.emoji}
                </span>

                <div className="flex flex-wrap items-center gap-1.5 mb-3" data-id="element-73">
                  <span className="inline-block text-primary font-black text-[11px] sm:text-xs bg-primary/10 px-2.5 py-1 rounded-full border border-primary/20 uppercase tracking-wider" data-id="element-74">
                    {item.pages}
                  </span>
                  <span className="inline-block text-cta-green font-bold text-[11px] sm:text-xs bg-cta-green/10 px-2.5 py-1 rounded-full border border-cta-green/20" data-id="element-75">
                    {item.format}
                  </span>
                </div>

                <h3 className="text-lg sm:text-xl font-black text-text-main leading-snug mb-2" data-id="element-76">
                  {item.title}
                </h3>
                <p className="text-text-sec text-sm sm:text-base leading-relaxed" data-id="element-77">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </div>

        {/* Carousel Section */}
        <div className="mt-16 sm:mt-20 relative" data-id="element-78">
          <div className="text-center mb-8 sm:mb-10" data-id="element-79">
            <p className="inline-block bg-cta-green/10 text-cta-green font-bold uppercase tracking-wider text-[10px] sm:text-xs px-3 py-1 rounded-full border border-cta-green/20 mb-3" data-id="element-80">
              📸 Imágenes reales del material — sin simulaciones
            </p>
            <h3 className="text-2xl sm:text-3xl font-black text-text-main" data-id="element-81">
              Mira el material por dentro
            </h3>
            <p className="text-text-sec text-sm mt-3 sm:hidden inline-flex items-center justify-center gap-1.5" data-id="element-82">
              <ChevronLeft className="w-4 h-4 animate-pulse" data-id="element-83" />
              Desliza para ver más
              <ChevronRight className="w-4 h-4 animate-pulse" data-id="element-84" />
            </p>
          </div>

          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-85">
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-white border border-border-light p-3 rounded-full text-text-main transition-all shadow-[0_8px_20px_-6px_rgba(30,64,175,0.15)] ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Anterior" data-id="element-86">
              <ChevronLeft className="w-5 h-5" data-id="element-87" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-alt hover:bg-white border border-border-light p-3 rounded-full text-text-main transition-all shadow-[0_8px_20px_-6px_rgba(30,64,175,0.15)] ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Siguiente" data-id="element-88">
              <ChevronRight className="w-5 h-5" data-id="element-89" />
            </button>

            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-175px)] pb-8 pt-4" data-id="element-90">
              {carouselImages.map((img, i) => <div key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[350px] bg-bg-alt border border-border-light rounded-2xl overflow-hidden p-2 sm:p-3 flex items-center justify-center shadow-[0_12px_30px_-10px_rgba(30,64,175,0.15)] transition-all duration-300 ${activeIndex === i ? 'scale-100 opacity-100 border-primary/40' : 'scale-95 opacity-50'}`} data-id="element-91">
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[400px] sm:max-h-[450px] object-contain rounded-xl bg-white" data-id="element-92" />
                </div>)}
            </div>

            {/* Counter pill */}
            <div className="flex justify-center mb-2" data-id="element-93">
              <span className="bg-bg-alt border border-border-light text-text-sec text-xs font-bold px-3 py-1 rounded-full" data-id="element-94">
                {activeIndex + 1} / {carouselImages.length}
              </span>
            </div>
          </div>

          <div className="flex justify-center gap-1 mt-2" data-id="element-95">
            {carouselImages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className="p-3 flex items-center justify-center" aria-label={`Ir a imagen ${i + 1}`} data-id="element-96">
                <span className={`block h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8' : 'bg-border-light hover:bg-text-sec w-2'}`} data-id="element-97" />
              </button>)}
          </div>
        </div>

        {/* Inline CTA */}
      </div>
    </section>;
}